<template>
  <div>
    <h1>Lista de pedidos</h1>

    <b-card>
      <div class="w-100 mb-2 d-inline-flex">
        <b-input-group>
          <b-form-input
            v-model="termo_pesquisa"
            @keyup.enter="searchTerm"
            placeholder="Filtre por ID, Cliente, CPF/CNPJ, Plano..."
          />
          <b-input-group-append>
            <b-button @click="searchTerm" variant="exitos">Buscar</b-button>
          </b-input-group-append>
        </b-input-group>
        <b-button class="ml-2" @click="toggleVisibleFilters">
          <font-awesome-icon icon="sliders" />
        </b-button>
      </div>
      <b-collapse v-model="isVisibleFilters" class="pb-2">
        <advanced-filters @search="advancedSearch" @clear="advancedSearch" />
      </b-collapse>
      <b-table
        striped
        hover
        :items="pedidos"
        :fields="fields"
        show-empty
        :busy="isBusy"
        @sort-changed="sortingChanged"
      >
        <template #cell(cliente)="{ item }">
          {{ item.user?.name || "-" }}
          <span
            v-if="!!item?.nota_antecipada"
            class="p ml-2 px-2 py-1 rounded-pill badge-nota-status"
          >
            <font-awesome-icon icon="exclamation" size="lg" />
            <font-awesome-icon
              icon="file-invoice-dollar"
              size="lg"
              class="main-icon"
            />
            <span class="mr-1 show-on-hover"> Nota antecipada </span>
          </span>
        </template>

        <template #cell(plano)="{ item }">
          <span class="text-break">
            {{ item.plano?.nome || "-" }}
          </span>
        </template>

        <template #cell(valor_pedido)="{ value, item }">
          <div class="text-gray-dark font-weight-600 mr-2">
            R$ {{ value | number_format }}
            <div
              class="text-capitalize-first font-weight-normal d-inline-block periodicy-label"
              v-if="item?.periodicidade"
              v-text="item?.periodicidade?.periodicidade"
            />
          </div>
        </template>

        <template #cell(forma_pagamento)="{ value, item }">
          <div class="d-inline-flex align-items-center">
            <div
              v-text="value"
              class="text-capitalize-first bg-exitos text-white px-2 py-1 rounded-pill max-status"
            />
            <div
              v-if="!!item?.ultimo_comprovante_url"
              title="Comprovante anexado"
              v-b-tooltip.hover
              class="ml-2"
            >
              <font-awesome-icon
                icon="fa-solid fa-paperclip"
                class="text-warning"
              />
            </div>
          </div>
        </template>

        <template #cell(status)="{ value }">
          <div class="d-inline-flex w-100 justify-content-center">
            <div
              :class="value.class"
              class="font-weight-bolder px-2 lh-1 text-uppercase rounded-sm text-center max-status"
              v-text="value.label"
            />
          </div>
        </template>

        <template #cell(updated_at)="{ value }">
          <div class="d-flex flex-row text-dark">
            <span>{{ value | format_date("DD/MM/YYYY") }}</span>
            <span class="ml-1">{{ value | format_date("HH:mm") }}</span>
          </div>
        </template>

        <template #cell(actions)="{ item }">
          <Actions
            :item="item"
            view_edit
            :view_show="false"
            :view_delete="false"
            edit_icon="fa-solid fa-eye"
            edit_to="pedidos_show"
            :edit_params="{ id: item.id }"
          />
        </template>

        <template #empty="{}">
          <h5 class="text-center">Nenhum item encontrado</h5>
        </template>
        <template #table-busy>
          <div class="text-center my-2">
            <b-spinner class="align-middle"></b-spinner>
          </div>
        </template>
      </b-table>
    </b-card>

    <div>
      <pagination-nav :pagination="pagination" @input="loadData()" />
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";

import Actions from "@/components/Table/Actions.vue";
import { tableMixins } from "@/components/Table/mixins";
import { toggleValue } from "@/mixins/toggle";
import AdvancedFilters from "@/modules/pedidos/Components/AdvancedFilters.vue";
import { getParsedStatus } from "@/modules/pedidos/Components/getParsedStatus";
import { toUpper } from "@/helpers/functions/StringHelpers";

export default {
  mixins: [tableMixins({ search: "busca" }), toggleValue("visible filters")],
  components: { AdvancedFilters, Actions },
  computed: {
    ...mapState("pedidos", ["pedidos", "pagination", "error"]),
    fields() {
      return [
        {
          key: "id",
          label: "COD",
          class: "align-middle col-1 text-center",
          sortable: true,
          formatter: (value) => `#${value}`,
        },
        {
          key: "cliente",
          label: "CLIENTE",
          class: "col-auto align-middle text-break",
          sortable: true,
        },
        {
          key: "plano",
          label: "PLANO ESCOLHIDO",
          class: "col-2 align-middle",
          sortable: true,
        },
        {
          key: "valor_pedido",
          label: "VALOR",
          class: "col-2 align-middle text-center",
          sortable: true,
          formatter: (value) => (Number.isNaN(value) ? value : Number(value)),
        },
        {
          key: "forma_pagamento",
          label: "Método",
          class: "col-1 align-middle",
        },
        {
          key: "updated_at",
          label: "ULT. ATUALIZ.",
          class: "col-1 align-middle",
        },
        {
          key: "status",
          label: "STATUS",
          class: "col-1 align-middle text-center",
          formatter: (value) => getParsedStatus(toUpper(value)),
        },
        { key: "actions", label: "", class: "align-middle cell-actions" },
      ];
    },
  },
  methods: {
    ...mapActions("pedidos", ["ActionGetAll", "ActionSetPagination"]),
    async advancedSearch(params = {}) {
      try {
        this.isBusy = true;

        const payload = {
          page: this.pagination.current_page,
          per_page: this.pagination.per_page,
          orderBy: this.sortBy,
          sortBy: this.sortDesc ? "desc" : "asc",
          busca: this.termo_pesquisa,
          ...params,
        };

        await this.ActionGetAll(payload);
      } finally {
        this.isBusy = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.max-status {
  max-width: fit-content;
}

.periodicy-label {
  position: relative;
  padding-left: 5px;

  &::after {
    content: "/";
    position: absolute;
    left: 0;
  }
}

.badge-nota-status {
  background-color: rgba(34, 83, 115, 0.1);
  color: var(--exitos);

  .main-icon {
    margin-left: 2px;
  }

  .show-on-hover {
    display: none;
  }

  &:hover {
    .show-on-hover {
      display: inline-block;
      margin-left: 4px;
    }
  }
}

::v-deep .bg-info-custom {
  background-color: #deb10d !important;
}
</style>
